<template>
  <el-container class="screenHeight">
    <el-header>
      <el-page-header @back="handleBack">
        <template #title>
          {{ $t('components.embossing-footer.backButton') }}
        </template>

        <template #content>
          {{ $t(`enum.upload-progress.${activeUploadProgress}`) }}
        </template>
      </el-page-header>
    </el-header>
    <el-main>
      <MeshLoader ref="meshLoader" v-model:uploadProgress="activeUploadProgress" v-model:formData="loadingData" :template-id="templateId">
        <template #form>
          <OrderMeshUploadForm
            v-model:loadingData="loadingData"
            :template-id="templateId"
            :directory-path="authentication.user?.username"
            :onUploadObject3D="(t: FileType3D, d: UploadData) => this.meshLoader.uploadObject3D(t, d)"
            :onRemoveObject3D="(uid: number) => this.meshLoader.removeObject3D(uid)"
          />
        </template>
      </MeshLoader>
    </el-main>
    <Workflow :workflow-model="workflowModel" :fullscreen-loading="fullscreenLoading" />
  </el-container>
</template>

<script lang="ts">
import { Vue, Component, Prop, Watch } from 'vue-facing-decorator';

import MeshLoader from '@/components/three/scenes/MeshLoader.vue';
import { FileType3D, UploadCategory } from '@/types/enum/upload';
import { UploadProgress } from '@/types/enum/workflow';
import * as LayoutUtility from '@/utils/layout';
import * as OrderService from '@/services/api/orderWorkflowService';
import Workflow from '@/components/workflow/Workflow.vue';
import { WorkflowModel } from '@/types/ui/WorkflowModel';
import { useRoute, useRouter } from 'vue-router';
import OrderMeshUploadForm, { ComponentMeshUploadData } from '@/components/forms/OrderMeshUploadForm.vue';
import Auth from '@/services/api/auth';
import * as TemplateService from '@/services/api/templateService';
import { UploadData } from '@/types/interfaces/projectionSidebarData';

@Component({
  components: {
    MeshLoader,
    OrderMeshUploadForm,
    Workflow,
  },
})
/* eslint-disable @typescript-eslint/no-explicit-any*/

export default class OrderUpload extends Vue {
  @Prop() templateId!: number;
  @Prop() orderId!: number;

  router = useRouter();
  authentication = Auth.getInstance();
  workflowModel: WorkflowModel<UploadProgress> = new WorkflowModel('upload-progress', UploadProgress);
  fullscreenLoading = false;

  loadingData: ComponentMeshUploadData = {
    category: UploadCategory.Product,
    title: '',
    fileType: FileType3D.NONE,
    url: '',
    base64: '',
    referenceCategory: UploadCategory.ReferenceDummy,
    referenceId: '',
    referenceData: [],
  };

  get meshLoader(): MeshLoader {
    return this.$refs.meshLoader as MeshLoader;
  }

  get activeUploadProgress(): UploadProgress {
    if (this.workflowModel.active < this.workflowModel.stepCount) {
      LayoutUtility.refresh();
      return Object.values(UploadProgress)[this.workflowModel.active];
    }
    return UploadProgress.Pivot;
  }

  set activeUploadProgress(value: UploadProgress) {
    this.workflowModel.setStep(Object.values(UploadProgress).indexOf(value));
  }

  async mounted(): Promise<void> {
    if (this.templateId) {
      try {
        const result = await TemplateService.getMeshData(this.templateId);
        this.loadingData.referenceData = result;
      } catch (error) {
        console.error('Failed to get template mesh data:', error);
      }
    }
  }

  @Watch('loadingData.referenceId', { immediate: true })
  onReferenceIdChanged(): void {
    if (this.templateId && this.loadingData.referenceId) {
      const referenceItem = this.loadingData.referenceData.find((item) => item.uniqueKey === this.loadingData.referenceId);
      if (referenceItem) {
        this.meshLoader.updateReferenceItem(referenceItem);
      }
    }
  }

  @Watch('workflowModel.finished', { immediate: true })
  onStepFinished(): void {
    if (this.workflowModel.finished) {
      this.fullscreenLoading = true;
      setTimeout(() => {
        const loader = this.$refs.meshLoader as MeshLoader;
        if (loader) {
          const result = loader.getResult();
          if (result)
            OrderService.addMeshData(this.orderId, this.templateId, result.category, result.title, result.mesh, result.bones, null, result.referenceId).then(
              () => {
                setTimeout(() => {
                  this.router.go(-1);
                }, 100);
              }
            );
        }
      }, 100);
    }
  }

  handleBack(): void {
    this.router.push({
      name: 'order-workflow',
      query: { templateId: this.templateId, orderId: this.orderId },
    });
  }
}
</script>

<style lang="scss" scoped>
.el-main :deep() {
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: auto;
}

.level:not(:last-child) {
  margin-bottom: unset;
}

.screenHeight {
  display: flex;
  flex-direction: column;
  height: 93vh;
}

.el-footer {
  display: flex;
  justify-content: center;
  align-items: center;
}

:deep(.el-steps) {
  width: 100%;
  flex: 1;
}
</style>
